/**
 * Native Alarm Sound Player
 * 
 * Uses a native iOS AVAudioPlayer via a Capacitor plugin to play alarm/timer
 * sounds reliably — even when the mute switch is on and even when the screen
 * is locked. This is the same technique used by the iOS Clock/Alarm app.
 * 
 * Falls back to Web Audio API on non-native platforms (web browser).
 */

import { Capacitor, registerPlugin } from '@capacitor/core'

// ─── Plugin interface ───────────────────────────────────────────────────────

interface AlarmSoundPlugin {
  play(options: { sound: string; volume?: number; loops?: number }): Promise<{ playing: boolean }>
  stop(): Promise<{ stopped: boolean }>
  addListener(event: 'playbackFinished', handler: (data: { success: boolean }) => void): Promise<{ remove: () => void }>
}

// Register the native plugin (Capacitor auto-discovers it on iOS via @objc(AlarmSoundPlugin))
const NativeAlarmSound = registerPlugin<AlarmSoundPlugin>('AlarmSoundPlugin')

// ─── Public API ─────────────────────────────────────────────────────────────

/**
 * Play a pomodoro timer alarm sound.
 * On iOS: uses native AVAudioPlayer (works on mute, works with screen off).
 * On web: falls back to Web Audio API.
 * 
 * @param sound   - filename of the sound bundled in the iOS app (e.g. "gong.wav")
 * @param volume  - 0.0 – 1.0 (default 0.8)
 */
export async function playAlarmSound(sound: string, volume = 0.8): Promise<void> {
  if (Capacitor.getPlatform() === 'ios') {
    try {
      await NativeAlarmSound.play({ sound, volume })
    } catch (error) {
      console.warn('[AlarmSound] Native play failed, sound may not have played:', error)
    }
  } else {
    // Non-iOS: no native alarm support, caller should use Web Audio fallback
    console.debug('[AlarmSound] Not on iOS, skipping native alarm sound')
  }
}

/**
 * Stop any currently-playing alarm sound.
 */
export async function stopAlarmSound(): Promise<void> {
  if (Capacitor.getPlatform() === 'ios') {
    try {
      await NativeAlarmSound.stop()
    } catch (error) {
      console.warn('[AlarmSound] Native stop failed:', error)
    }
  }
}

/**
 * Check whether native alarm sound is available (iOS only).
 */
export function isNativeAlarmAvailable(): boolean {
  return Capacitor.getPlatform() === 'ios'
}
